import * as Yup from 'yup';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { store } from 'react-notifications-component';
import branchApi from "../../../store/branchStore/branchApi";

export const useBonusReferral = (t) => {
    const [error, setError] = useState("");
    const { info } = useSelector(s => s.branchReducer);
    const [receivers, setReceivers] = useState([])
    const [config, setConfig] = useState(null)
    const [saving, setSaving] = useState(false)
    const [isAccessEnabled, setIsAccessEnabled] = useState(false)
    const [errorModal, setErrorModal] = useState(false)

    useEffect(() => {
        branchApi.getBranchReferralConfig(info?.id).then(
            (res) => {
                setConfig(res.data)
            }
        ).catch(
            (err) => {
                setError(err.message)
            }
        )
    }, [info?.id]);

    useEffect(() => {
        if (info && info.configuration && info.configuration.access_toggles && info.configuration.access_toggles.referrals) {
            setIsAccessEnabled(info.configuration.access_toggles.referrals.enabled || false)
        }
    }, [info]);

    const formik = useFormik({
        initialValues: {
            expire_interval: { label: 'withoutTerm', value: 0 },
            invite_text: "",
            min_purchase_amount: 0,
            referee_fixed_amount: 0,
            referee_percentage: 0,
            referrer_fixed_amount: 0,
            referrer_percentage: 0,
            transactions_limit: 0,
            type: "FIXED",
        },
        validationSchema: Yup.object({
        }),
        onSubmit: (async (values) => {
            try {
                setSaving(true)

                const payload = {
                    type: values.type,
                    transactions_limit: Number(values.transactions_limit) || 0,
                    min_purchase_amount: Number(values.min_purchase_amount) || 0,
                    invite_text: values.invite_text || undefined,
                }

                // Send expire_interval as milliseconds (DurationStr treats numbers as ms)
                // When "Без срока" is selected (value=0), send ~100 years to represent infinite
                const ONE_HUNDRED_YEARS_MS = 100 * 365.25 * 24 * 60 * 60 * 1000;
                payload.expire_interval = values.expire_interval?.value || ONE_HUNDRED_YEARS_MS;

                if (values.type === 'FIXED') {
                    payload.referrer_fixed_amount = receivers.includes('CLIENT') ? Number(values.referrer_fixed_amount) || 0 : 0;
                    payload.referee_fixed_amount = receivers.includes('FRIEND') ? Number(values.referee_fixed_amount) || 0 : 0;
                } else if (values.type === 'PERCENTAGE') {
                    payload.referrer_percentage = receivers.includes('CLIENT') ? Number(values.referrer_percentage) || 0 : 0;
                    payload.referee_percentage = receivers.includes('FRIEND') ? Number(values.referee_percentage) || 0 : 0;
                }

                // Validate that selected receivers have non-zero bonus amounts
                const hasInvalidAmount =
                    (values.type === 'FIXED' && receivers.includes('CLIENT') && !(Number(values.referrer_fixed_amount) > 0)) ||
                    (values.type === 'FIXED' && receivers.includes('FRIEND') && !(Number(values.referee_fixed_amount) > 0)) ||
                    (values.type === 'PERCENTAGE' && receivers.includes('CLIENT') && !(Number(values.referrer_percentage) > 0)) ||
                    (values.type === 'PERCENTAGE' && receivers.includes('FRIEND') && !(Number(values.referee_percentage) > 0));

                if (hasInvalidAmount) {
                    store.addNotification({
                        message: t('Referral.bonusAmountRequired'),
                        type: "warning",
                        insert: "top",
                        container: "top-right",
                        animationIn: ["animate__animated", "animate__fadeIn"],
                        animationOut: ["animate__animated", "animate__fadeOut"],
                        dismiss: {
                            duration: 5000,
                            onScreen: true
                        }
                    });
                    return;
                }

                await branchApi.putBranchReferralConfig(info?.id, payload)
                store.addNotification({
                    message: t('Notify.success'),
                    type: "success",
                    insert: "top",
                    container: "top-right",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 5000,
                        onScreen: true
                    }
                })
            } catch (e) {
                const res = e.response ? e.response.data : null;

                store.addNotification({
                    title: `Error code: ${res ? res.code : 'NULL'}`,
                    message: `${res ? res.message : t('Notify.notRespond')}`,
                    type: "danger",
                    insert: "top",
                    container: "top-right",
                    animationIn: ["animate__animated", "animate__fadeIn"],
                    animationOut: ["animate__animated", "animate__fadeOut"],
                    dismiss: {
                        duration: 5000,
                        onScreen: true
                    }
                });

            } finally {
                setSaving(false)
            }
        })
    });

    useEffect(() => {
        formik.setErrors({});
        formik.setTouched({});
    }, [info]);

    // Prefill form from loaded config if available
    useEffect(() => {
        if (config) {
            const cfg = config || {};

            const award = cfg.award || {};
            const messageConfig = cfg.message_config || {};

            let expireOpt = { label: 'withoutTerm', value: 0 };
            const options = [
                { label: 'withoutTerm', value: 0 },
                { label: 'month', value: 2.628e+9 },
                { label: 'twoWeeks', value: 1.2096e+9 },
                { label: 'two', value: 2*2.628e+9 },
                { label: 'three', value: 3*2.628e+9 },
                { label: 'halfYear', value: 6*2.628e+9 },
                { label: 'year', value: 3.154e+10 },
                { label: 'oneAndHalfYear', value: 1.5*3.154e+10 },
                { label: 'twoYears', value: 2*3.154e+10 },
            ];
            // Convert nanoseconds to milliseconds (divide by 1,000,000)
            const expireIntervalMs = cfg.expire_interval ? cfg.expire_interval / 1000000 : 0;
            const found = options.find(o => expireIntervalMs == o.value);
            if (found) expireOpt = found;

            formik.setValues({
                expire_interval: expireOpt,
                invite_text: messageConfig.invite_text || '',
                min_purchase_amount: cfg.min_purchase_amount || 0,
                referee_fixed_amount: award.referee_fixed_amount || 0,
                referee_percentage: award.referee_percentage || 0,
                referrer_fixed_amount: award.referrer_fixed_amount || 0,
                referrer_percentage: award.referrer_percentage || 0,
                transactions_limit: cfg.transactions_limit || 0,
                type: award.type || 'FIXED',
            });

            // Auto-select receivers based on award amounts
            const autoReceivers = [];

            // Check if CLIENT (referrer/inviter) should receive bonus
            if (award.type === 'FIXED' && award.referrer_fixed_amount > 0) {
                autoReceivers.push('CLIENT');
            } else if (award.type === 'PERCENTAGE' && award.referrer_percentage > 0) {
                autoReceivers.push('CLIENT');
            }

            // Check if FRIEND (referee/invitee) should receive bonus
            if (award.type === 'FIXED' && award.referee_fixed_amount > 0) {
                autoReceivers.push('FRIEND');
            } else if (award.type === 'PERCENTAGE' && award.referee_percentage > 0) {
                autoReceivers.push('FRIEND');
            }

            setReceivers(autoReceivers);
        }
    }, [config]);

    return {
        formik,
        pending: saving,
        errorModal,
        setErrorModal,
        receivers,
        setReceivers,
        isAccessEnabled,
    }
}
